import { useState } from 'react'
import { useLanguage } from '../context/LanguageContext'
import { cn } from '../lib/utils'

// 5 levels of investment growth
const INVESTMENT_LEVELS = [
  { level: 1, message: { es: 'Inicio temprano', en: 'Very early start' } },
  { level: 2, message: { es: 'Buen inicio', en: 'Good start' } },
  { level: 3, message: { es: 'Construyendo progreso', en: 'Making progress' } },
  { level: 4, message: { es: 'Ahorro sustancial', en: 'Substantial savings' } },
  { level: 5, message: { es: '¡Meta alcanzada!', en: 'Goal achieved!' } },
]

// Premium 3D Gold Coin with brushed matte finish
const GoldCoin = ({ size = 32, shadow = true }) => (
  <div 
    className="relative flex-shrink-0"
    style={{ 
      width: size, 
      height: size * 0.38,
    }}
  >
    <div 
      className="absolute inset-x-0 bottom-0 rounded-[50%]"
      style={{
        height: '100%',
        background: `linear-gradient(180deg, 
          #C9A962 0%, 
          #B8944A 25%,
          #A07D3A 50%, 
          #8A6B2E 75%,
          #755A25 100%
        )`,
        boxShadow: shadow 
          ? '0 3px 6px rgba(90,70,30,0.25), 0 1px 3px rgba(0,0,0,0.1)' 
          : 'none'
      }}
    />
    <div 
      className="absolute inset-x-0 top-0 rounded-[50%]"
      style={{
        height: '68%',
        background: `linear-gradient(135deg, 
          #E8D9A8 0%,
          #DECE98 10%,
          #D4C088 25%, 
          #CBB67A 40%,
          #C2AC6C 55%,
          #B9A260 70%,
          #B09855 85%,
          #A78E4A 100%
        )`,
        boxShadow: `
          inset 2px 2px 6px rgba(255,255,255,0.35),
          inset -1px -1px 4px rgba(140,110,50,0.15)
        `
      }}
    >
      <div 
        className="absolute rounded-[50%]"
        style={{
          top: '18%',
          left: '18%',
          right: '18%',
          bottom: '18%',
          border: '1px solid rgba(180,150,80,0.15)',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 50%)'
        }}
      />
    </div>
  </div>
)

// Clean coin stack - vertical pile
const CoinStack = ({ count, coinSize = 32 }) => {
  const overlap = coinSize * 0.22
  const totalHeight = count > 0 ? (coinSize * 0.38) + (count - 1) * (coinSize * 0.38 - overlap) : 0
  
  return (
    <div 
      className="relative"
      style={{ 
        width: coinSize,
        height: totalHeight
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="absolute left-0"
          style={{
            bottom: i * (coinSize * 0.38 - overlap),
            zIndex: i + 1
          }}
        >
          <GoldCoin size={coinSize} shadow={i === count - 1} />
        </div>
      ))}
    </div>
  )
}

// Level 1: Small pile - early start
const Level1Stack = () => (
  <div className="flex items-end justify-center gap-2">
    <CoinStack count={2} coinSize={28} />
    <CoinStack count={1} coinSize={28} />
  </div>
)

// Level 2: Single tall stack - building
const Level2Stack = () => (
  <div className="flex items-end justify-center">
    <CoinStack count={10} coinSize={26} />
  </div>
)

// Level 3: Multiple stacks - making progress
const Level3Stack = () => (
  <div className="flex items-end justify-center gap-1.5">
    <CoinStack count={5} coinSize={24} />
    <CoinStack count={9} coinSize={24} />
    <CoinStack count={6} coinSize={24} />
  </div>
)

// Level 4: Larger formation - substantial
const Level4Stack = () => (
  <div className="flex items-end justify-center gap-1">
    <CoinStack count={4} coinSize={22} />
    <CoinStack count={7} coinSize={22} />
    <CoinStack count={10} coinSize={22} />
    <CoinStack count={7} coinSize={22} />
    <CoinStack count={4} coinSize={22} />
  </div>
)

// Level 5: Premium pyramid - goal achieved
const Level5Stack = () => (
  <div className="flex items-end justify-center gap-0.5">
    <CoinStack count={3} coinSize={20} />
    <CoinStack count={6} coinSize={20} />
    <CoinStack count={9} coinSize={20} />
    <CoinStack count={11} coinSize={20} />
    <CoinStack count={9} coinSize={20} />
    <CoinStack count={6} coinSize={20} />
    <CoinStack count={3} coinSize={20} />
  </div>
)

const renderStack = (level) => {
  switch (level) {
    case 1: return <Level1Stack />
    case 2: return <Level2Stack />
    case 3: return <Level3Stack />
    case 4: return <Level4Stack />
    case 5: return <Level5Stack />
    default: return <Level1Stack />
  }
}

export default function InvestmentLevelsDemo() {
  const { locale } = useLanguage()
  const isSpanish = locale === 'es-MX'

  return (
    <div className="p-6 bg-gradient-to-br from-[#F8F4EC] to-[#EDE6D6] min-h-screen">
      <h1 className="text-2xl font-bold text-[#4A3D2A] mb-2 text-center">
        {isSpanish ? 'Niveles de Inversión' : 'Investment Levels'}
      </h1>
      <p className="text-sm text-[#8B7355] mb-8 text-center">
        {isSpanish ? '5 niveles basados en el ratio inversión/gastos' : '5 levels based on investment/expenses ratio'}
      </p>
      
      <div className="flex flex-wrap justify-center gap-4">
        {INVESTMENT_LEVELS.map((levelInfo) => (
          <div
            key={levelInfo.level}
            className={cn(
              "relative flex flex-col items-center gap-3 px-5 py-4 rounded-2xl",
              "bg-gradient-to-br from-[#FAF6EE] via-[#F5F0E4] to-[#EDE6D6]",
              "border border-[#E5DCC8]/60",
              "min-w-[150px]"
            )}
            style={{
              boxShadow: `
                inset 0 0 40px rgba(237,230,214,0.5),
                0 4px 16px rgba(150,130,90,0.1),
                0 2px 6px rgba(0,0,0,0.04)
              `
            }}
          >
            {/* Level badge */}
            <div className="absolute top-2 right-3 text-[#B8A070] text-xs font-medium opacity-60">
              Lv.{levelInfo.level}
            </div>
            
            {/* Coin visualization */}
            <div className="min-h-[80px] flex items-end justify-center pt-2">
              {renderStack(levelInfo.level)}
            </div>
            
            {/* Level message */}
            <p className="text-xs font-semibold text-[#5C4D35] text-center">
              {isSpanish ? levelInfo.message.es : levelInfo.message.en}
            </p>
            
            {/* Ratio range */}
            <span className="text-[10px] text-[#A89878] font-medium">
              {levelInfo.level === 1 && '0% - 3%'}
              {levelInfo.level === 2 && '3% - 7%'}
              {levelInfo.level === 3 && '7% - 12%'}
              {levelInfo.level === 4 && '12% - 20%'}
              {levelInfo.level === 5 && '≥20%'}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
